/**
 * RosterWeekCell.tsx
 * ONE (person, week) cell of the roster-timeline capacity hero, after the
 * "Staff this job" split-availability-bar redesign (2026-07-14,
 * qa/staff-this-job-redesign-2026-07-14/SPEC.md §2). Three cases:
 *   1. EDITABLE project-window cell — the faint task context, the
 *      CapacitySplitBar, and the uniform ± typeable stepper (step 20%, press +
 *      on 0% → 100%) + a "remove" link (disabled at 0%).
 *   2. READ-ONLY "beyond project" cell (future horizon) — the split bar only,
 *      dashed styling, no editing controls — availability context so a
 *      scheduler can see where a busy person frees up.
 *   3. READ-ONLY project-window cell (plan locked/Submitted) — the split bar,
 *      solid styling, no editing controls.
 * Clicking the bar opens the shared breakdown popover (CapacityCellPopover) —
 * `onOpenDetail` is always wired regardless of case.
 *
 * Presentational: the parent (StaffRosterTimeline) owns the write + the 0%
 * confirm + the popover state. Colors via theme tokens — no hex literals
 * (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { CapacitySplitBar } from './CapacitySplitBar';
import { PercentStepperInput } from './PercentStepperInput';
import { staffHair, STAFF_CELL_RADIUS } from './staffViewStyle';
import type { RosterCellModel } from './staffRosterTimelineModel';
import type { SplitVisibility } from './capacitySplitModel';

export interface RosterWeekCellProps {
  model: RosterCellModel;
  /** Show the ± stepper + remove link (project-window week AND plan not locked). */
  editable: boolean;
  /** Beyond the task span — always read-only, dashed "availability outlook" styling. */
  isFutureWeek: boolean;
  /** A task is assigned on a day this person has off — the one hard signal. */
  hasConflict?: boolean;
  /** Which split-bar segments the "Show" toolbar chips currently render. */
  visibility: SplitVisibility;
  /** Commit a new this-job % (the parent turns 0 into a remove-from-job confirm). */
  onSetPercent: (percent: number) => void;
  /** Open the breakdown popover, anchored at the clicked element. */
  onOpenDetail: (e: React.MouseEvent<HTMLElement>) => void;
}

export const RosterWeekCell: React.FC<RosterWeekCellProps> = ({
  model,
  editable,
  isFutureWeek,
  hasConflict = false,
  visibility,
  onSetPercent,
  onOpenDetail,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        m: 0.9,
        px: 1,
        py: 0.9,
        borderRadius: `${STAFF_CELL_RADIUS}px`,
        backgroundColor: hasConflict
          ? alpha(theme.schedule.heatmapHigh, 0.06)
          : isFutureWeek ? 'transparent' : theme.schedule.selectionBg,
        boxShadow: hasConflict
          ? `inset 0 0 0 2px ${theme.schedule.heatmapHigh}`
          // Future cells use just the dashed border (no doubled inner border).
          : isFutureWeek
            ? 'none'
            : `inset 0 0 0 1px ${staffHair(theme)}`,
        border: isFutureWeek ? `1px dashed ${staffHair(theme)}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0.5,
        minHeight: isFutureWeek ? 64 : 96,
      }}
    >
      {/* The assigned-task list is NOT shown in the cell any more (it broke the
          fixed box size — 2026-07-14 feedback). It now lives in the breakdown
          popover instead. Every cell is the split bar (+ stepper when editable). */}

      {/* The split availability bar. Editable/project cells always show it; a
          FULLY-FREE future cell skips it (an empty track floating in the dashed
          box read as an off-centre rectangle — 2026-07-14 feedback), leaving just
          the centred availability label. Future cells WITH load still show it. */}
      {(!isFutureWeek || model.committedPercent + model.uncommittedPercent + model.thisJobPercent > 0) && (
        <CapacitySplitBar
          committedPercent={model.committedPercent}
          uncommittedPercent={model.uncommittedPercent}
          minePercent={model.thisJobPercent}
          visibility={visibility}
          showCaption={!isFutureWeek}
          onOpenDetail={onOpenDetail}
        />
      )}

      {/* The uniform this-project % stepper (editable project-window cells only). */}
      {editable && (
        <PercentStepperInput value={model.thisJobPercent} onChange={onSetPercent} />
      )}
      {editable && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            component="button"
            type="button"
            onClick={() => onSetPercent(0)}
            disabled={model.thisJobPercent <= 0}
            sx={{
              border: 0, background: 'none', p: 0, fontSize: '0.62rem', textDecoration: 'underline',
              color: 'text.secondary', cursor: model.thisJobPercent > 0 ? 'pointer' : 'default',
              opacity: model.thisJobPercent > 0 ? 1 : 0.35,
              '&:hover': model.thisJobPercent > 0 ? { color: theme.schedule.heatmapHigh } : undefined,
            }}
          >
            remove
          </Typography>
        </Box>
      )}

      {isFutureWeek && (
        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', textAlign: 'center', fontStyle: 'italic', lineHeight: 1.3 }}>
          {model.overPercent > 0
            ? 'over-booked'
            : model.freePercent >= 100
              ? 'fully free'
              : model.freePercent > 0
                ? `${model.freePercent}% free`
                : 'booked elsewhere'}
        </Typography>
      )}

      {hasConflict && (
        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, textAlign: 'center', lineHeight: 1, color: theme.schedule.heatmapHigh }}>
          day-off conflict
        </Typography>
      )}
    </Box>
  );
};
